import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { EntitlementsService } from '../service/entitlements.service';

/**
 * SD-1218: blocks direct navigation to a route whose module is not entitled.
 *
 * Reads `data.requiredModule` from the route. A route without one is always allowed, so this
 * guard is safe to add anywhere.
 *
 * Modelled on SuperAdminGuard, which returns the piped observable. Explicitly *not* modelled
 * on SettingsGuard, which subscribes asynchronously and then returns synchronously -- so its
 * role field is always still 0 when the check runs and it returns true every time. That bug
 * is why module.guard.spec.ts asserts this guard returns the observable rather than a bare
 * boolean.
 *
 * This is convenience and tidiness, not security: a determined user can still call the API
 * directly. Server-side [RequireModule] is the real boundary.
 */
@Injectable({ providedIn: 'root' })
export class ModuleGuard implements CanActivate, CanActivateChild, CanLoad {

  constructor(private entitlements: EntitlementsService, private router: Router) { }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.check(route && route.data ? route.data.requiredModule : null);
  }

  canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.check(route && route.data ? route.data.requiredModule : null);
  }

  /**
   * Angular 11 has no `canMatch`, so CanLoad is the only way to keep a lazy chunk unfetched.
   * Note the router caches a CanLoad result for the session: a module granted mid-session
   * will not become reachable until a full page reload. That is why routes use canActivate by
   * default and add canLoad only where withholding the JavaScript is worth that trade.
   */
  canLoad(route: Route): Observable<boolean> {
    return this.check(route && route.data ? route.data.requiredModule : null);
  }

  private check(requiredModule: string): Observable<boolean> {
    if (!requiredModule) {
      return of(true);
    }

    return this.entitlements.has$(requiredModule).pipe(
      take(1),
      tap(allowed => {
        if (!allowed) {
          // /dashboard is never module-gated, so this cannot loop.
          this.router.navigate(['/dashboard']);
        }
      })
    );
  }
}
